import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc, 
  collection, 
  query, 
  getDocs,
  updateDoc,
  deleteDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { generateOTP, formatPhoneNumber } from '../config/africastalking';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [otpSent, setOtpSent] = useState(false);
  const [pendingOTP, setPendingOTP] = useState(null);
  const [pendingCredentials, setPendingCredentials] = useState(null);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Fetch user profile from Firestore
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserProfile(userDoc.data());
        }
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Step 1: Validate email/password and send OTP
  const initiateLogin = async (email, password) => {
    try {
      // First verify credentials are correct by signing in
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Get user profile to get phone number
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      
      if (!userDoc.exists()) {
        await signOut(auth);
        throw new Error('User profile not found. Please contact admin.');
      }

      const userData = userDoc.data();
      const phoneNumber = formatPhoneNumber(userData.phone);
      
      // Generate OTP
      const otp = generateOTP();
      const otpExpiry = Date.now() + 5 * 60 * 1000; // 5 minutes
      
      // Store OTP in Firestore (in production, use a more secure method)
      await setDoc(doc(db, 'otps', userCredential.user.uid), {
        otp: otp,
        expiry: otpExpiry,
        phone: phoneNumber,
        createdAt: serverTimestamp()
      });

      // Send SMS via Africa's Talking
      // Note: In sandbox mode, this will appear in the simulator
      await sendOTPSMS(phoneNumber, otp, userData.name);

      // Sign out temporarily until OTP is verified
      await signOut(auth);

      // Store pending credentials
      setPendingCredentials({ email, password, uid: userCredential.user.uid });
      setPendingOTP(otp); // For sandbox testing - remove in production
      setOtpSent(true);

      return { 
        success: true, 
        phone: phoneNumber.replace(/(\+254)(\d{3})(\d{3})(\d{3})/, '$1***$3$4'),
        // In sandbox mode, return OTP for testing
        sandboxOTP: otp 
      };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Send OTP via Africa's Talking
  const sendOTPSMS = async (phone, otp, name) => {
    // In a production app, this should be done via a secure backend/cloud function
    // For now, we'll use a serverless function approach
    
    const message = `Dear ${name}, your Remington LLP Finance Portal OTP is: ${otp}. Valid for 5 minutes. Do not share this code.`;
    
    try {
      // Using Africa's Talking API via a proxy or cloud function
      // For sandbox testing, the SMS appears in the AT simulator
      
      // Store the message in Firestore for logging
      await setDoc(doc(db, 'sms_logs', `${Date.now()}`), {
        to: phone,
        message: message,
        otp: otp,
        status: 'pending',
        createdAt: serverTimestamp()
      });

      console.log(`OTP SMS queued for ${phone}: ${otp}`);
      
      // In production, call your backend API to send SMS
      // await fetch('/api/send-sms', { method: 'POST', body: JSON.stringify({ phone, message }) });
      
      return true;
    } catch (error) {
      console.error('SMS sending error:', error);
      throw new Error('Failed to send OTP. Please try again.');
    }
  };

  // Step 2: Verify OTP and complete login
  const verifyOTPAndLogin = async (enteredOTP) => {
    if (!pendingCredentials) {
      throw new Error('No pending login. Please start again.');
    }

    try {
      // Verify OTP from Firestore
      const otpDoc = await getDoc(doc(db, 'otps', pendingCredentials.uid));
      
      if (!otpDoc.exists()) {
        throw new Error('OTP expired or invalid. Please try again.');
      }

      const otpData = otpDoc.data();

      if (Date.now() > otpData.expiry) {
        await deleteDoc(doc(db, 'otps', pendingCredentials.uid));
        throw new Error('OTP has expired. Please login again.');
      }

      if (otpData.otp !== enteredOTP) {
        throw new Error('Invalid OTP. Please try again.');
      }

      // OTP is valid - complete login
      await signInWithEmailAndPassword(auth, pendingCredentials.email, pendingCredentials.password);
      
      // Clean up
      await deleteDoc(doc(db, 'otps', pendingCredentials.uid));
      setPendingCredentials(null);
      setPendingOTP(null);
      setOtpSent(false);

      return { success: true };
    } catch (error) {
      console.error('OTP verification error:', error);
      throw error;
    }
  };

  // Cancel OTP process
  const cancelOTP = () => {
    setPendingCredentials(null);
    setPendingOTP(null);
    setOtpSent(false);
  };

  // Logout
  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      setUserProfile(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  // Create new user (Admin only)
  const createUser = async (userData) => {
    if (userProfile?.role !== 'admin') {
      throw new Error('Only admins can create users.');
    }

    try {
      // Create auth user
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        userData.email, 
        userData.temporaryPassword
      );

      // Create user profile in Firestore
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email: userData.email,
        name: userData.name,
        phone: formatPhoneNumber(userData.phone),
        role: userData.role || 'staff',
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        active: true
      });

      // Sign back in as admin (creating user signs us in as new user)
      // In production, use Admin SDK via cloud functions instead
      
      return { success: true, uid: userCredential.user.uid };
    } catch (error) {
      console.error('Create user error:', error);
      throw error;
    }
  };

  // Get all users (Admin only)
  const getAllUsers = async () => {
    if (userProfile?.role !== 'admin') {
      throw new Error('Only admins can view all users.');
    }

    try {
      const usersQuery = query(collection(db, 'users'));
      const snapshot = await getDocs(usersQuery);
      
      return snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Get users error:', error);
      throw error;
    }
  };

  // Update user (Admin only)
  const updateUser = async (uid, updates) => {
    if (userProfile?.role !== 'admin') {
      throw new Error('Only admins can update users.');
    }

    try {
      await updateDoc(doc(db, 'users', uid), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Update user error:', error);
      throw error;
    }
  };

  // Deactivate user (Admin only)
  const deactivateUser = async (uid) => {
    if (userProfile?.role !== 'admin') {
      throw new Error('Only admins can deactivate users.');
    }

    try {
      await updateDoc(doc(db, 'users', uid), {
        active: false,
        deactivatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Deactivate user error:', error);
      throw error;
    }
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    otpSent,
    pendingOTP, // For sandbox testing only
    initiateLogin,
    verifyOTPAndLogin,
    cancelOTP,
    logout,
    createUser,
    getAllUsers,
    updateUser,
    deactivateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
